import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { Ticket, ExternalLink, Download } from 'lucide-react';
import { API_BASE } from '../config';
import { formatDate as formatDateShared } from '../utils/formatDate';
import { hasEditAccess } from '../permissionConfig';
import { notifyEditBlocked } from '../components/EditAccessToast';
import { useSwrStore, useSwrQuery } from '../swrCache';
// Aliased: this page already has a `toDate` state variable (the "To" date
// filter), which would shadow the import inside the component.
import { toDate as excelDate, num, exportSheet, todayStamp } from '../excel';
import DataFreshness from '../components/DataFreshness';

const COLS = [
  ['STATUS','center'], ['PO DATE','center'], ['PARTY NAME','left','260px'], ['PO NUMBER','center'], ['LOCATION','center'],
  ['PO UNITS','right'], ['PO AMOUNT','right'], ['PO EXPIRY','center'], ['KAM','center'],
  ['CHANNEL','center'], ['REMARK','center'], ['SO DATE','center'], ['VOUCHER NO','center'],
  ['INVOICE NO','center'], ['INVOICE QTY','right'], ['INVOICE AMOUNT','right'],
  ['BOX COUNT','right'], ['BOX WEIGHT (KG)','right'],
  ['AWB NUMBER','center'], ['TRACKING STATUS','center'], ['POD','center','110px'],
  ['GRN NO','center'], ['GRN DATE','center'],
  ['GRN QTY','right'], ['GRN STATUS','center'], ['ACTIONS','center'],
];

// Loose keyword match on Shiprocket's free-text status field — not an exact
// enum (their CSV/webhooks use varying casing/wording), so this just buckets
// into good/bad/neutral rather than trying to match every exact string.
function trackingStatusStyle(status) {
  const s = (status || '').toLowerCase();
  if (s.includes('deliver')) return { bg: 'linear-gradient(#E4F1E7,#C3DEC9)', fg: '#2C5138', border: '#93B79A' };
  if (s.includes('rto') || s.includes('ndr') || s.includes('cancel')) return { bg: 'linear-gradient(#F8E6E6,#EFC7C7)', fg: '#7A2E2E', border: '#D9A3A3' };
  if (s === 'pending') return { bg: 'linear-gradient(#F3EFE6,#DDD5C4)', fg: '#6E6038', border: '#BFB49B' };
  return { bg: 'linear-gradient(#E4ECF9,#C4D5EE)', fg: '#2F4A72', border: '#94ADD1' };
}

// Same keyword buckets as trackingStatusStyle above, but as a filter key
// instead of a color — kept as one place both read from a real change to
// Shiprocket's status text would need updating in.
function trackingBucket(status) {
  const s = (status || '').toLowerCase();
  if (s === 'pending' || !s) return 'pending';
  if (s.includes('deliver')) return 'delivered';
  if (s.includes('rto') || s.includes('ndr') || s.includes('cancel')) return 'issue';
  return 'transit';
}

const TRACKING_LABELS = {
  delivered: 'Delivered',
  transit: 'In Transit',
  issue: 'RTO / NDR',
  pending: 'Pending',
};

// Colors for the unified lifecycle Status badge (STATUS column) — computed
// server-side in po-tracker.js's GET query from the same SO/Invoice/GRN
// joins already used for the REMARK/GRN STATUS columns, so this can show
// >2000-precedence order Cancelled > Invoiced > Sales Order Made > GRN
// Received > Open without any new write-path.
const STATUS_STYLE = {
  'Cancelled':                    { bg:'linear-gradient(#F7DEDB,#EFC5C0)', fg:'#A33B33', border:'#DDA39D' },
  'Invoiced':                     { bg:'linear-gradient(#E4ECF9,#C4D5EE)', fg:'#2F4A72', border:'#94ADD1' },
  'Sales Order Made':             { bg:'linear-gradient(#E4F1E7,#C3DEC9)', fg:'#2C5138', border:'#93B79A' },
  'GRN Received':                 { bg:'linear-gradient(#EFE8F9,#D8CBEF)', fg:'#4A3777', border:'#B7A3DE' },
  'Awaiting Distributor Approval':{ bg:'linear-gradient(#F5EDDC,#E9D9B5)', fg:'#7A5F2C', border:'#D3BD8C' },
  'Open':                         { bg:'linear-gradient(#F3EFE6,#DDD5C4)', fg:'#6E6038', border:'#BFB49B' },
};

// Which COLS headers hold dates / numbers, and which mapRow field carries the
// raw value for each. Used only by the Excel export (see exportExcel): the
// on-screen cells keep their display formatting, while the file gets the raw
// stored values so dates sort as dates and amounts can be SUMmed.
const DATE_COL_SOURCES = {
  'PO DATE':   'poDateRaw',
  'PO EXPIRY': 'expiryRaw',
  'SO DATE':   'soDateRaw',
  'GRN DATE':  'grnDateRaw',
};

const NUM_COL_SOURCES = {
  'PO UNITS':        'unitsRaw',
  'PO AMOUNT':       'amountRaw',
  'INVOICE QTY':     'invoiceQtyRaw',
  'INVOICE AMOUNT':  'invoiceAmountRaw',
  'BOX COUNT':       'boxCountRaw',
  'BOX WEIGHT (KG)': 'boxWeightRaw',
  'GRN QTY':         'grnQtyRaw',
};

// PO NUMBER / VOUCHER NO / INVOICE NO / GRN NO are deliberately NOT in
// NUM_COL_SOURCES even though they often look numeric. A 13-digit PO number
// like 1723710055140 displays as 1.72371E+12 once Excel treats it as a number,
// and anything past 15 digits loses precision outright — and nobody does
// arithmetic on an identifier.

const MONTHS = { Jan:0, Feb:1, Mar:2, Apr:3, May:4, Jun:5, Jul:6, Aug:7, Sep:8, Oct:9, Nov:10, Dec:11 };
const NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
// CLAY is the neumorphic surface color — deliberately the same value as the
// page background, since that's what makes the raised/inset shadows read as
// sculpted rather than as floating cards. Set to #FAF8FC to match the page
// background used behind content everywhere else in the portal (see
// POSOLifecycleLayout.jsx and the other domain layouts), so this page no
// longer sits on a distinctly purple field next to the sidebar.
const TEXT = '#2B2340', MUTED = '#6B6379', CLAY = '#FAF8FC';

const TODAY = new Date().toISOString().slice(0, 10);
const CURRENT_YEAR = new Date().getFullYear();

// The font the sidebar (and the rest of the portal) actually renders in.
// Sidebar.jsx sets no fontFamily of its own, so it inherits `body`, which
// App.css sets to this stack — App.css is imported after index.css, so it
// wins over index.css's system-font stack. Used for the filter/controls area
// so those controls match the sidebar instead of the design's Poppins.
const UI_FONT = "'Segoe UI', Calibri, sans-serif";

const RAISED = '5px 5px 10px rgba(120,102,168,0.24), -4px -4px 9px rgba(255,255,255,0.95)';
const INSET = 'inset 4px 4px 9px rgba(120,102,168,0.28), inset -4px -4px 9px rgba(255,255,255,0.95)';
const PRESSED = 'inset 4px 4px 9px rgba(0,0,0,0.16), inset -3px -3px 8px rgba(255,255,255,0.55)';

// ── Display helpers ──────────────────────────────────────────────────────
// Returns null (not '—') for missing/invalid — several call sites below
// rely on this falsy-check to conditionally render at all.
function formatDate(value) {
  if (!value) return null;
  const formatted = formatDateShared(value);
  return formatted === '—' ? null : formatted;
}

// "BHIWANDI" / "bhiwandi" -> "Bhiwandi"
function formatLocation(loc) {
  if (!loc) return loc;
  const trimmed = String(loc).trim();
  if (!trimmed) return trimmed;
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
}

function parseNum(val) {
  if (val === null || val === undefined || val === '') return null;
  const n = parseFloat(String(val).replace(/,/g, ''));
  return isNaN(n) ? null : n;
}

const parse = (d) => {
  const m = /(\d{2}) (\w{3}) (\d{4})/.exec(d || '');
  return m ? new Date(+m[3], MONTHS[m[2]], +m[1]) : null;
};
const toISO = (d) => {
  const p = parse(d);
  return p ? `${p.getFullYear()}-${String(p.getMonth() + 1).padStart(2, '0')}-${String(p.getDate()).padStart(2, '0')}` : '';
};

// API row (po-tracker.js's joined shape) -> this page's display shape.
// `id` is kept alongside so Edit can PUT back to the real po_headers row.
// so/inv reflect whether a matching SO/Invoice record exists at all; grn
// reflects the PO being fully reconciled (grn_management.status === 'Matched'
// specifically) — a PO with a GRN that's still "Open"/"Resolved" shows as
// GRN Pending here, not Matched, since this page's badge is meant to answer
// "is this PO fully done," not just "has some GRN arrived."
function mapRow(r) {
  return {
    id: r.id,
    // The *Raw fields keep the unformatted values purely for the Excel export,
    // which needs real dates and real numbers rather than the display strings
    // beside them (those carry ₹, thousands separators and '—' placeholders).
    // The display fields are what the table renders and are unchanged.
    poDateRaw: r.po_date,
    expiryRaw: r.po_expiry_date,
    soDateRaw: r.so_date,
    grnDateRaw: r.grn_date,
    unitsRaw: r.po_units,
    amountRaw: r.po_amount,
    invoiceQtyRaw: r.invoice_qty,
    invoiceAmountRaw: r.invoice_amount,
    boxCountRaw: r.box_count,
    boxWeightRaw: r.box_weight,
    grnQtyRaw: r.grn_qty,
    date: formatDate(r.po_date) || '—',
    party: r.party_name || '—',
    po: r.po_number,
    loc: r.location ? formatLocation(r.location) : '—',
    units: r.po_units ?? '—',
    amount: r.po_amount != null ? '₹' + Number(r.po_amount).toLocaleString('en-IN') : '—',
    expiry: formatDate(r.po_expiry_date) || '—',
    kam: r.kam_name || '—',
    channel: r.channel || '—',
    remark: r.order_type || '—',
    so: !!r.voucher_no,
    inv: !!r.invoice_no,
    grn: r.grn_status === 'Matched',
    soDate: formatDate(r.so_date),
    voucher: r.voucher_no,
    invoiceNo: r.invoice_no,
    invoiceQty: r.invoice_qty,
    invoiceAmount: r.invoice_amount != null ? '₹' + Number(r.invoice_amount).toLocaleString('en-IN') : null,
    boxCount: r.box_count,
    boxWeight: r.box_weight != null ? Number(r.box_weight).toFixed(1) : null,
    grnNo: r.grn_no,
    grnDate: formatDate(r.grn_date),
    grnQty: r.grn_qty,
    awbNumber: r.awb_number || '—',
    trackingStatus: r.tracking_status || 'Pending',
    hasPod: !!r.pod_blob_name,
    status: r.status,
    lifecycleStatus: r.lifecycle_status,
    poSource: r.po_source,
    statusRemark: r.status_remark,
    updatedBy: r.updated_by,
    updatedAt: r.updated_at,
  };
}

const field = { padding:'10px 13px', border:'none', borderRadius:13, background:CLAY, fontSize:13, color:TEXT, fontFamily:UI_FONT, outline:'none', boxShadow:INSET };
const label = { fontSize:10.5, fontWeight:700, letterSpacing:'0.09em', color:'#8C82A0', fontFamily:UI_FONT };

function Segment({ options, value, onChange }) {
  return (
    <div style={{ display:'flex', gap:7 }}>
      {options.map((o) => {
        const active = value === o.value;
        return (
          <button key={o.value} onClick={() => onChange(o.value)} style={{
            padding:'8px 15px', border:'none', borderRadius:12, fontSize:12.5, fontWeight:600, cursor:'pointer',
            fontFamily:UI_FONT, whiteSpace:'nowrap',
            background: active ? o.bg : CLAY, color: active ? o.fg : '#8C82A0',
            boxShadow: active ? PRESSED : RAISED,
          }}>{o.label}</button>
        );
      })}
    </div>
  );
}

export default function POTracker({ user }) {
  // Date range defaults to today, and is sent to the BACKEND (not filtered
  // client-side) — po-tracker.js's GET caps at 2000 rows ordered by po_date
  // DESC, so client-side date filtering could only ever search within the
  // most recent 2000 POs and would silently return nothing for any older
  // month/year. Server-side filtering is what makes the date controls work
  // across the full history.
  const [fromDate, setFromDate] = useState(TODAY);
  const [toDate, setToDate] = useState(TODAY);
  // Month/Year are not a separate filter dimension — selecting them computes
  // and sets the From/To range above (same pattern as GRNManagement.jsx), so
  // there's exactly one date dimension and the two can never contradict.
  const [month, setMonth] = useState('all');
  const [year, setYear] = useState('all');
  const [search, setSearch] = useState('');
  // Debounced copy of `search` sent to the backend (see rangeKey below) —
  // typed on every keystroke but only triggers a network call 350ms after
  // the user stops typing, same idea as GRNManagement's search debounce.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);
  const [so, setSo] = useState('all');
  const [inv, setInv] = useState('all');
  const [grn, setGrn] = useState('all');
  const [tracking, setTracking] = useState('all');
  // 'default' ("All") shows every status, Cancelled included; picking a
  // specific status (including 'Cancelled') filters exactly to it.
  const [status, setStatus] = useState('default');
  const [expiring, setExpiring] = useState(false);
  const [toast, setToast] = useState(false);
  const [podDownloading, setPodDownloading] = useState(false);
  const [podDownloadMsg, setPodDownloadMsg] = useState(null);
  const [editPo, setEditPo] = useState(null);
  const [editId, setEditId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [actionPo, setActionPo] = useState(null); // { r, kind: 'cancel'|'delete' }
  const [actionReason, setActionReason] = useState('');
  const [actionSaving, setActionSaving] = useState(false);
  const [actionMsg, setActionMsg] = useState('');
  const [podModalPo, setPodModalPo] = useState(null);
  const timer = useRef();

  useEffect(() => () => clearTimeout(timer.current), []);

  // The date range (plus, since 2026-10-19, the debounced search text) is
  // what triggers a server call on this page — status/expiring filtering
  // still happens client-side over the fetched rows below.
  //
  // Search is now part of the cache key/query, not just a client-side
  // filter: po-tracker.js caps results at 2000 rows (ORDER BY po_date DESC)
  // for performance, so on a wide date range with >2000 matching POs, an
  // older PO past that cutoff was silently never fetched at all — searching
  // for it client-side found nothing even though it exists and matches
  // every filter, with no indication anything had been cut off. Sending
  // `search` lets the backend's WHERE narrow the result BEFORE the cap
  // applies, so a known PO number reliably surfaces regardless of how many
  // other POs are ahead of it in the date range.
  //
  // Cap of 8 (vs 30 on Sales Orders): each entry here can hold up to 2000
  // mapped rows — po-tracker.js's row cap — so entries are far heavier than a
  // 50-row page. Eight covers realistic back-and-forth across a few months
  // without holding tens of thousands of row objects in memory.
  const store = useSwrStore(8);

  const rangeKey = useMemo(() => {
    const qs = new URLSearchParams();
    if (fromDate) qs.set('date_from', fromDate);
    if (toDate)   qs.set('date_to', toDate);
    if (debouncedSearch) qs.set('search', debouncedSearch);
    return qs.toString();
  }, [fromDate, toDate, debouncedSearch]);

  const poQuery = useSwrQuery(store, rangeKey, useCallback(async () => {
    const res  = await fetch(`${API_BASE}/po-tracker${rangeKey ? '?' + rangeKey : ''}`);
    const data = await res.json();
    if (!res.ok) throw new Error(data?.message || `Server returned ${res.status}`);
    return Array.isArray(data.rows) ? data.rows.map(mapRow) : [];
  }, [rangeKey]));

  // Memoised so the identity is stable while data is unchanged — otherwise the
  // `|| []` allocates a new array every render and re-runs the filtering below.
  const apiRows = useMemo(() => poQuery.data || [], [poQuery.data]);
  const loading = poQuery.loading;

  // Add/Edit PO clears the WHOLE cache, not just the current range: an edited
  // PO's date can move it out of the range on screen and into a different
  // cached one, which would then keep showing the pre-edit values.
  const reloadData = useCallback(async () => {
    store.clear();
    await poQuery.refresh();
  }, [store, poQuery]);

  // Deep-link from a notification click (?q=<po_number>) — pre-fills the
  // existing search box, same convention used across the rest of the app.
  // Also clears the default today-only date range: the linked PO is very
  // often NOT from today, and leaving the range in place would filter the
  // very row the notification is pointing at straight out of view.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search).get('q');
    if (q) {
      setSearch(q);
      setFromDate('');
      setToDate('');
    }
  }, []);

  const copy = (po) => {
    if (navigator.clipboard) navigator.clipboard.writeText(po);
    setToast(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setToast(false), 1800);
  };

  const soon = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return d;
  }, []);

  // If the search text is an exact match (case-insensitive) for a PO number,
  // voucher number, or invoice number anywhere in the fetched range, narrow
  // to just that one identifier instead of the broader "contains" search —
  // typing a full PO/voucher/invoice number should isolate that single
  // record, not surface every row that happens to contain those characters
  // elsewhere (party name, location, etc). A short/partial query naturally
  // won't exact-match anything, so it falls through to the normal
  // multi-row "contains" behavior below.
  const exactSearchMatch = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return false;
    return apiRows.some(r =>
      r.po?.toLowerCase() === q || r.voucher?.toLowerCase() === q || r.invoiceNo?.toLowerCase() === q
    );
  }, [apiRows, search]);

  // Date range is already applied server-side (see poQuery). Everything
  // below is a client-side AND filter over that fetched range — all of them
  // combine, since each one only ever excludes rows.
  const rows = useMemo(() => {
    return apiRows.filter((r) => {
      if (so !== 'all' && (so === 'yes') !== r.so) return false;
      if (inv !== 'all' && (inv === 'yes') !== r.inv) return false;
      if (grn !== 'all' && (grn === 'yes') !== r.grn) return false;
      if (tracking !== 'all' && trackingBucket(r.trackingStatus) !== tracking) return false;
      if (status !== 'default' && r.lifecycleStatus !== status) return false;
      if (expiring) { const e = parse(r.expiry); if (!e || e > soon) return false; }
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        if (exactSearchMatch) {
          const isExact = r.po?.toLowerCase() === q || r.voucher?.toLowerCase() === q || r.invoiceNo?.toLowerCase() === q;
          if (!isExact) return false;
        } else if (!`${r.party}${r.po}${r.loc}${r.kam}${r.channel}`.toLowerCase().includes(q)) {
          return false;
        }
      }
      return true;
    });
  }, [apiRows, so, inv, grn, tracking, status, expiring, search, soon, exactSearchMatch]);

  const openEdit = (r) => {
    setAdding(false);
    setEditPo(r.po);
    setEditId(r.id);
    setSaveMsg('');
    setDraft({
      date: toISO(r.date), po: r.po, party: r.party === '—' ? '' : r.party,
      loc: r.loc === '—' ? '' : r.loc,
      units: r.units === '—' ? '' : r.units,
      amount: r.amount === '—' ? '' : String(r.amount).replace(/[₹,]/g, ''),
      expiry: toISO(r.expiry),
    });
  };
  const openAdd = () => {
    setEditPo(null);
    setEditId(null);
    setAdding(true);
    setSaveMsg('');
    setDraft({ date:'', po:'', party:'', loc:'', units:'', amount:'', expiry:'' });
  };
  const closeModal = () => { setEditPo(null); setAdding(false); setSaveMsg(''); };
  const setD = (k, v) => setDraft((p) => ({ ...p, [k]: v }));

  const openAction = (r, kind) => { setActionPo({ r, kind }); setActionReason(''); setActionMsg(''); };
  const closeAction = () => { setActionPo(null); setActionReason(''); setActionMsg(''); };

  async function submitAction() {
    if (!actionPo) return;
    const reason = actionReason.trim();
    if (!reason) { setActionMsg('A reason is required'); return; }

    const { r, kind } = actionPo;
    setActionSaving(true);
    setActionMsg('');
    try {
      const res = await fetch(`${API_BASE}/po-tracker?action=${kind}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(kind === 'cancel'
          ? { id: r.id, reason, updated_by: user?.username || null }
          : { id: r.id, reason, deleted_by: user?.username || null }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        await reloadData();
        closeAction();
      } else {
        setActionMsg(data.message || `Failed to ${kind}`);
      }
    } catch (e) {
      setActionMsg('Network error');
    } finally {
      setActionSaving(false);
    }
  }

  async function save() {
    const poNumber = String(draft.po || '').trim();
    if (!poNumber) { setSaveMsg('PO Number is required'); return; }

    const payload = {
      po_number: poNumber,
      po_date: draft.date || null,
      party_name: draft.party || null,
      location: draft.loc ? formatLocation(draft.loc) : null,
      po_units: draft.units !== '' ? parseNum(draft.units) : null,
      po_amount: draft.amount !== '' ? parseNum(draft.amount) : null,
      po_expiry_date: draft.expiry || null,
    };

    setSaving(true);
    setSaveMsg('');
    try {
      if (adding) {
        const res = await fetch(`${API_BASE}/po-tracker`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ rows: [{ ...payload, created_by: user?.username || 'Manual Entry' }] }),
        });
        const data = await res.json();
        if (res.ok && data.success && data.inserted > 0) {
          await reloadData();
          closeModal();
        } else {
          setSaveMsg('PO Number already exists');
        }
      } else {
        const res = await fetch(`${API_BASE}/po-tracker`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: editId, ...payload, updated_by: user?.username || null }),
        });
        const data = await res.json();
        if (res.ok && data.success) {
          await reloadData();
          closeModal();
        } else {
          setSaveMsg(data.message || 'Failed to save');
        }
      }
    } catch (e) {
      setSaveMsg('Network error');
    } finally {
      setSaving(false);
    }
  }

  const chips = [];
  if (fromDate || toDate) {
    chips.push(fromDate === TODAY && toDate === TODAY
      ? 'Today'
      : `${fromDate ? formatDate(fromDate) : 'any'} → ${toDate ? formatDate(toDate) : 'any'}`);
  } else {
    chips.push('All dates');
  }
  if (so !== 'all') chips.push(so === 'yes' ? 'SO Made' : 'SO Pending');
  if (inv !== 'all') chips.push(inv === 'yes' ? 'Invoiced' : 'Not Invoiced');
  if (grn !== 'all') chips.push(grn === 'yes' ? 'GRN Done' : 'GRN Pending');
  if (tracking !== 'all') chips.push(TRACKING_LABELS[tracking]);
  if (status !== 'default') chips.push(`Status: ${status}`);
  if (expiring) chips.push('Expiring in 7 days');

  // Month/Year -> date range, so they feed the same server-side filter the
  // From/To inputs do instead of fighting with it. Same helper shape as
  // GRNManagement.jsx's applyMonthYear.
  function applyMonthYear(m, y) {
    if (m === 'all' && y === 'all') { setFromDate(''); setToDate(''); return; }
    const yr = y === 'all' ? CURRENT_YEAR : Number(y);
    if (m !== 'all') {
      const mo = Number(m);
      setFromDate(`${yr}-${String(mo).padStart(2, '0')}-01`);
      setToDate(new Date(yr, mo, 0).toISOString().slice(0, 10)); // day 0 of next month = last day of this one
    } else {
      setFromDate(`${yr}-01-01`);
      setToDate(`${yr}-12-31`);
    }
  }

  const clear = () => {
    setFromDate(TODAY); setToDate(TODAY); setMonth('all'); setYear('all'); setSearch('');
    setSo('all'); setInv('all'); setGrn('all'); setTracking('all'); setStatus('default'); setExpiring(false);
  };

  function exportExcel() {
    const headers = COLS.slice(0, -1).map(([lab]) => lab); // drop ACTIONS

    // Text columns keep coming from cellsFor(), so the file reads the same as
    // the screen. Date and number columns are then swapped for their raw stored
    // values, matched BY HEADER LABEL rather than by index — reordering or
    // inserting a column in COLS would otherwise silently start formatting the
    // wrong cells.
    const dataRows = rows.map((r) => {
      const cells = cellsFor(r).map((c) => c.text);
      headers.forEach((lab, i) => {
        if (DATE_COL_SOURCES[lab])     cells[i] = excelDate(r[DATE_COL_SOURCES[lab]]);
        else if (NUM_COL_SOURCES[lab]) cells[i] = num(r[NUM_COL_SOURCES[lab]]);
        // '—' is a UI placeholder for "no value", not data — blank it out so the
        // spreadsheet has empty cells to filter on instead of literal dashes.
        else if (cells[i] === '—')     cells[i] = null;
      });
      return cells;
    });

    exportSheet({
      headers,
      rows: dataRows,
      sheetName: 'PO Tracker',
      filename: `PO_Tracker_${todayStamp()}.xlsx`,
    });
  }

  // Bulk POD download for whatever's currently on screen — respects every
  // active filter (date range, month/year, search, SO/invoice/GRN/tracking/
  // status, expiry) since it's built from `rows`, the same client-side
  // filtered array the table itself renders, not a fresh server query.
  //
  // The backend caps a single zip at 100 files / ~150MB (see po-tracker.js —
  // a wider request crashed the Function host outright before that cap was
  // added). Rather than making the user manually narrow filters until it fits,
  // split the PO list into batches under that cap and download each batch as
  // its own zip ("PO_Tracker_PODs_part1.zip", "_part2.zip", …) — one click
  // still gets everything for the current filter, just as multiple files.
  const POD_BATCH_SIZE = 60;

  async function downloadPods() {
    const poNumbers = [...new Set(rows.filter(r => r.hasPod).map(r => r.po))];
    if (poNumbers.length === 0) { setPodDownloadMsg('No PODs available for the current filters'); return; }

    const batches = [];
    for (let i = 0; i < poNumbers.length; i += POD_BATCH_SIZE) batches.push(poNumbers.slice(i, i + POD_BATCH_SIZE));

    setPodDownloading(true); setPodDownloadMsg(null);
    const failures = [];
    try {
      for (let i = 0; i < batches.length; i++) {
        if (batches.length > 1) setPodDownloadMsg(`Downloading part ${i + 1} of ${batches.length}…`);
        try {
          const res = await fetch(`${API_BASE}/po-tracker?action=download_pods`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ po_numbers: batches[i] }),
          });
          if (!res.ok) {
            const text = await res.text().catch(() => 'Download failed');
            failures.push(batches.length > 1 ? `Part ${i + 1}: ${text}` : text);
            continue;
          }
          const dispositionMatch = (res.headers.get('content-disposition') || '').match(/filename="?([^"]+)"?/);
          const baseName = dispositionMatch ? dispositionMatch[1] : 'PO_Tracker_PODs.zip';
          const filename = batches.length > 1 ? baseName.replace(/\.zip$/, `_part${i + 1}.zip`) : baseName;
          const blob = await res.blob();
          const url  = window.URL.createObjectURL(blob);
          const a    = document.createElement('a');
          a.href = url; a.download = filename;
          document.body.appendChild(a); a.click(); a.remove();
          window.URL.revokeObjectURL(url);
        } catch {
          failures.push(batches.length > 1 ? `Part ${i + 1}: Network error` : 'Network error');
        }
      }
      setPodDownloadMsg(failures.length > 0 ? failures.join(' | ') : null);
    } finally {
      setPodDownloading(false);
    }
  }

  const cellsFor = (r) => {
    const e = parse(r.expiry);
    const txt = (text, align = 'center', fg = MUTED, weight = 400) => ({ kind:'text', text, align, fg, weight, wrap:'nowrap' });
    const badge = (text, bg, fg, border) => ({ kind:'badge', text, bg, fg, border, align:'center', wrap:'nowrap' });
    const statusStyle = STATUS_STYLE[r.lifecycleStatus] || STATUS_STYLE['Open'];
    return [
      badge(r.lifecycleStatus || 'Open', statusStyle.bg, statusStyle.fg, statusStyle.border),
      txt(r.date, 'center', TEXT, 600),
      { kind:'text', text:r.party, align:'left', fg:TEXT, weight:600, wrap:'normal', width:'260px' },
      { kind:'po', text:r.po, align:'center', wrap:'nowrap' },
      txt(r.loc),
      txt(r.units, 'right', TEXT, 600),
      txt(r.amount, 'right', TEXT, 600),
      txt(r.expiry, 'center', e && e <= soon ? '#B0553F' : '#9A7B3E', 600),
      txt(r.kam),
      txt(r.channel),
      r.remark === '—' ? txt('—')
        : badge(r.remark,
            r.remark === 'Sales Order' ? 'linear-gradient(#E4F1E7,#C3DEC9)' : 'linear-gradient(#E4ECF9,#C4D5EE)',
            r.remark === 'Sales Order' ? '#2C5138' : '#2F4A72',
            r.remark === 'Sales Order' ? '#93B79A' : '#94ADD1'),
      txt(r.soDate || '—'),
      txt(r.voucher || '—', 'center', TEXT, 600),
      txt(r.invoiceNo || '—', 'center', TEXT, 600),
      txt(r.invoiceQty ?? '—', 'right', TEXT),
      txt(r.invoiceAmount || '—', 'right', TEXT),
      txt(r.boxCount ?? '—', 'right', TEXT),
      txt(r.boxWeight || '—', 'right', TEXT),
      txt(r.awbNumber, 'center', TEXT, 600),
      (() => { const ts = trackingStatusStyle(r.trackingStatus); return badge(r.trackingStatus, ts.bg, ts.fg, ts.border); })(),
      { kind:'pod', text: r.hasPod ? 'Available' : 'Not Available', hasPod: r.hasPod, po: r.po, align:'center', wrap:'nowrap', width:'110px' },
      txt(r.grnNo || '—', 'center', TEXT),
      txt(r.grnDate || '—', 'center', MUTED),
      txt(r.grnQty ?? '—', 'right', TEXT, 600),
      badge(r.grn ? 'Matched' : 'Open',
        r.grn ? 'linear-gradient(#E4F1E7,#C3DEC9)' : 'linear-gradient(#F3EFE6,#DDD5C4)',
        r.grn ? '#2C5138' : '#6E6038',
        r.grn ? '#93B79A' : '#BFB49B'),
    ];
  };

  return (
    <div style={{ minHeight:'100vh', background:CLAY, color:TEXT, fontFamily:"'Poppins', sans-serif", padding:'22px 28px 26px', display:'flex', flexDirection:'column', gap:16 }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:20, flexWrap:'wrap' }}>
        <div style={{ fontSize:32, fontWeight:600, letterSpacing:'-0.025em' }}>PO Tracker</div>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <DataFreshness
            fetchedAt={poQuery.fetchedAt}
            loading={loading}
            onRefresh={reloadData}
            style={{ fontFamily:UI_FONT }}
          />
          <button
            type="button"
            data-tutorial="po-tracker-download-pod"
            onClick={downloadPods}
            disabled={podDownloading}
            title="Download every available POD for the current filters"
            style={{
              border: 0,
              background: '#FAF8FC',
              boxShadow: 'inset 2px 2px 5px #e4dced, inset -2px -2px 5px #ffffff',
              borderRadius: 999,
              padding: '5px 11px',
              font: 'inherit',
              fontSize: 12,
              fontWeight: 800,
              color: '#6c6376',
              cursor: podDownloading ? 'default' : 'pointer',
              opacity: podDownloading ? 0.55 : 1,
              fontFamily: UI_FONT,
            }}
          >
            {podDownloading ? 'Zipping…' : '⬇ Download POD'}
          </button>
        </div>
      </div>
      {podDownloadMsg && (
        <div style={{ padding:'9px 13px', borderRadius:11, background:'#F3D9CE', color:'#8A3E2B', fontSize:11.5, fontWeight:600 }}>{podDownloadMsg}</div>
      )}

      {/* FILTER PANEL */}
      <div style={{ display:'flex', flexDirection:'column', gap:15, padding:'20px 22px', borderRadius:22, background:CLAY, boxShadow:'inset 6px 6px 14px rgba(120,102,168,0.26), inset -6px -6px 14px rgba(255,255,255,0.95)' }}>
        <div style={{ display:'flex', flexWrap:'wrap', gap:14, alignItems:'flex-end' }}>
          <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
            <span style={label}>FROM</span>
            <input type="date" value={fromDate} onChange={(e) => { setMonth('all'); setYear('all'); setFromDate(e.target.value); }} style={{ ...field, boxShadow:RAISED }} />
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
            <span style={label}>TO</span>
            <input type="date" value={toDate} onChange={(e) => { setMonth('all'); setYear('all'); setToDate(e.target.value); }} style={{ ...field, boxShadow:RAISED }} />
          </div>
          <button onClick={clear} style={{ padding:'10px 16px', border:'none', borderRadius:13, background:CLAY, color:MUTED, fontWeight:600, fontSize:12.5, cursor:'pointer', fontFamily:UI_FONT, boxShadow:RAISED }}>Clear</button>

          <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
            <span style={label}>MONTH</span>
            <select value={month} onChange={(e) => { setMonth(e.target.value); applyMonthYear(e.target.value, year); }} style={{ ...field, boxShadow:RAISED, cursor:'pointer' }}>
              <option value="all">All Months</option>
              {NAMES.map((n, i) => <option key={n} value={String(i + 1).padStart(2,'0')}>{n}</option>)}
            </select>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
            <span style={label}>YEAR</span>
            <select value={year} onChange={(e) => { setYear(e.target.value); applyMonthYear(month, e.target.value); }} style={{ ...field, boxShadow:RAISED, cursor:'pointer' }}>
              <option value="all">All Years</option>
              <option value="2024">2024</option><option value="2025">2025</option><option value="2026">2026</option>
            </select>
          </div>
          <button onClick={() => { setMonth('all'); setYear('all'); setFromDate(TODAY); setToDate(TODAY); }} style={{ padding:'10px 17px', border:'none', borderRadius:13, background:TEXT, color:'#fff', fontWeight:600, fontSize:12.5, cursor:'pointer', fontFamily:UI_FONT, boxShadow:'5px 5px 11px rgba(43,35,64,0.34), -4px -4px 9px rgba(255,255,255,0.85)' }}>Today</button>

          <div style={{ display:'flex', flexDirection:'column', gap:6, marginLeft:'auto' }}>
            <span style={label}>SEARCH</span>
            <input type="text" placeholder="PO number, party, location…" value={search} onChange={(e) => setSearch(e.target.value)} style={{ ...field, width:230 }} />
          </div>
        </div>

        <div style={{ height:1, background:'rgba(120,102,168,0.18)' }} />

        <div style={{ display:'flex', flexWrap:'wrap', gap:20, alignItems:'flex-end' }}>
          <div style={{ display:'flex', flexDirection:'column', gap:7 }}>
            <span style={label}>SO STATUS</span>
            <Segment value={so} onChange={setSo} options={[
              { value:'all', label:'All', bg:'#C9BCEB', fg:'#3B2F63' },
              { value:'yes', label:'SO Made', bg:'#B9DCC3', fg:'#2C5138' },
              { value:'no', label:'SO Pending', bg:'#F0D3B5', fg:'#7A4E20' },
            ]} />
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:7 }}>
            <span style={label}>INVOICE STATUS</span>
            <Segment value={inv} onChange={setInv} options={[
              { value:'all', label:'All', bg:'#C9BCEB', fg:'#3B2F63' },
              { value:'yes', label:'Invoiced', bg:'#BFD3EE', fg:'#2F4A72' },
              { value:'no', label:'Not Invoiced', bg:'#EDCFE2', fg:'#6E3F60' },
            ]} />
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:7 }}>
            <span style={label}>GRN STATUS</span>
            <Segment value={grn} onChange={setGrn} options={[
              { value:'all', label:'All', bg:'#C9BCEB', fg:'#3B2F63' },
              { value:'yes', label:'GRN Done', bg:'#CFC4EC', fg:'#463777' },
              { value:'no', label:'GRN Pending', bg:'#E6DEC9', fg:'#6E6038' },
            ]} />
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:7 }}>
            <span style={label}>TRACKING STATUS</span>
            <Segment value={tracking} onChange={setTracking} options={[
              { value:'all', label:'All', bg:'#C9BCEB', fg:'#3B2F63' },
              { value:'delivered', label:'Delivered', bg:'#C3DEC9', fg:'#2C5138' },
              { value:'transit', label:'In Transit', bg:'#C4D5EE', fg:'#2F4A72' },
              { value:'issue', label:'RTO / NDR', bg:'#EFC7C7', fg:'#7A2E2E' },
              { value:'pending', label:'Pending', bg:'#DDD5C4', fg:'#6E6038' },
            ]} />
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:7 }}>
            <span style={label}>STATUS</span>
            <Segment value={status} onChange={setStatus} options={[
              { value:'default', label:'All', bg:'#C9BCEB', fg:'#3B2F63' },
              { value:'Open', label:'Open', bg:'#E6DEC9', fg:'#6E6038' },
              { value:'Sales Order Made', label:'SO Made', bg:'#B9DCC3', fg:'#2C5138' },
              { value:'GRN Received', label:'GRN Received', bg:'#D8CBEF', fg:'#4A3777' },
              { value:'Invoiced', label:'Invoiced', bg:'#BFD3EE', fg:'#2F4A72' },
              { value:'Cancelled', label:'Cancelled', bg:'#EFC5C0', fg:'#A33B33' },
            ]} />
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:7 }}>
            <span style={label}>EXPIRY</span>
            <button onClick={() => setExpiring(!expiring)} style={{
              padding:'8px 15px', border:'none', borderRadius:12, fontSize:12.5, fontWeight:600, cursor:'pointer', fontFamily:UI_FONT,
              background: expiring ? '#F0C9BE' : CLAY, color: expiring ? '#8A3E2B' : '#8C82A0', boxShadow: expiring ? PRESSED : RAISED,
            }}>Expiring in 7 days</button>
          </div>
        </div>
      </div>

      {/* COUNT + ACTIONS */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:15, padding:'0 4px' }}>
        <div style={{ display:'flex', alignItems:'baseline', gap:9 }}>
          <span style={{ fontSize:13, color:MUTED, fontFamily:UI_FONT }}>
            {loading ? 'Loading…' : <>Showing <strong style={{ color:TEXT }}>{rows.length}</strong> POs</>}
          </span>
          <span style={{ fontSize:12, color:'#8C82A0', fontFamily:UI_FONT }}>Active: {chips.join(' · ')}</span>
        </div>
        <div style={{ display:'flex', gap:10, flex:'none' }}>
          <button onClick={() => { if (!hasEditAccess(user, 'po_so_lifecycle', 'po_tracker')) return notifyEditBlocked(); openAdd(); }} style={{ padding:'11px 19px', border:'none', borderRadius:15, background:'#E9C77E', color:'#4A3612', fontWeight:600, fontSize:13, cursor:'pointer', fontFamily:UI_FONT, boxShadow:'6px 6px 13px rgba(150,120,60,0.32), -5px -5px 11px rgba(255,255,255,0.9)' }}>+ Add New PO</button>
          <button onClick={exportExcel} title="Download Excel" style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', width:40, height:40, padding:0, border:'none', borderRadius:14, background:CLAY, cursor:'pointer', boxShadow:'6px 6px 13px rgba(120,102,168,0.26), -5px -5px 11px rgba(255,255,255,0.95)' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3D7A4A" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v12" /><polyline points="7 11 12 16 17 11" /><path d="M4 20h16" /></svg>
          </button>
        </div>
      </div>

      {/* TABLE */}
      <div style={{ borderRadius:22, background:CLAY, padding:9, boxShadow:'10px 10px 22px rgba(120,102,168,0.28), -8px -8px 19px rgba(255,255,255,0.95)' }}>
        <div style={{ overflow:'auto', maxHeight:'calc(100vh - 60px)', borderRadius:11, border:'1px solid #B6A9D6', boxShadow:'inset 0 3px 8px rgba(80,60,130,0.28)' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', minWidth:2000 }}>
            <thead>
              <tr>
                {COLS.map(([lab, align, width]) => (
                  <th key={lab} style={{
                    position:'sticky', top:0, zIndex:3, background:'linear-gradient(#2A2530,#111014)', color:'#F5F2FA',
                    padding:'11px 11px', fontSize:10, fontWeight:700, letterSpacing:'0.08em', whiteSpace:'nowrap',
                    textAlign:align, minWidth:width || 'auto', borderRight:'1px solid #35313C',
                    textShadow:'0 -1px 0 rgba(0,0,0,0.45)', boxShadow:'inset 0 1px 0 rgba(255,255,255,0.22)',
                  }}>{lab}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const bg = i % 2 ? 'linear-gradient(#F1ECFA,#E7E1F4)' : 'linear-gradient(#FBF9FE,#F2EEFA)';
                return (
                  <tr key={r.id ?? r.po + i}>
                    {cellsFor(r).map((c, j) => (
                      <td key={j} style={{ padding:'10px 11px', background:bg, borderBottom:'1px solid #C7BBE2', borderRight:'1px solid #D7CEEB', textAlign:c.align, whiteSpace:c.wrap, minWidth:c.width || 'auto' }}>
                        {c.kind === 'badge' && (
                          <span style={{ display:'inline-block', padding:'4px 9px', borderRadius:5, fontSize:11, fontWeight:700, background:c.bg, color:c.fg, border:`1px solid ${c.border}`, boxShadow:'inset 0 1px 0 rgba(255,255,255,0.7), 0 1px 2px rgba(70,52,120,0.28)' }}>{c.text}</span>
                        )}
                        {c.kind === 'po' && (
                          <span style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'4px 10px', borderRadius:5, background:'linear-gradient(#FBF9FF,#E7E0F7)', border:'1px solid #B6A9D6', boxShadow:'inset 0 1px 0 rgba(255,255,255,0.95), 0 1px 2px rgba(70,52,120,0.22)' }}>
                            <span style={{ fontSize:12.5, fontWeight:700, color:'#5B4A93' }}>{c.text}</span>
                            <span onClick={() => copy(c.text)} style={{ cursor:'pointer', fontSize:11.5, color:'#9186B4' }}>⧉</span>
                          </span>
                        )}
                        {c.kind === 'text' && <span style={{ fontSize:12.5, color:c.fg, fontWeight:c.weight }}>{c.text}</span>}
                        {c.kind === 'pod' && (
                          c.hasPod ? (
                            <button
                              onClick={() => setPodModalPo(c.po)}
                              style={{ display:'inline-flex', alignItems:'center', gap:5, padding:'6px 14px', border:'none', borderRadius:8, background:'#6E58A6', color:'#fff', fontSize:11.5, fontWeight:700, cursor:'pointer', fontFamily:"'Poppins'", boxShadow:'0 2px 6px rgba(110,88,166,0.4)' }}
                            >
                              <Ticket size={13} />Click
                            </button>
                          ) : (
                            <span style={{ display:'inline-flex', alignItems:'center', gap:5, padding:'6px 14px', borderRadius:8, background:'#EDEAF3', color:'#B6AFC7', fontSize:11.5, fontWeight:600 }}>
                              <Ticket size={13} />—
                            </span>
                          )
                        )}
                      </td>
                    ))}
                    <td style={{ padding:'10px 11px', background:bg, borderBottom:'1px solid #C7BBE2', textAlign:'center' }}>
                      <div style={{ display:'flex', gap:6, justifyContent:'center', flexWrap:'wrap' }}>
                        <button onClick={() => { if (!hasEditAccess(user, 'po_so_lifecycle', 'po_tracker')) return notifyEditBlocked(); openEdit(r); }} style={{ padding:'6px 14px', border:'1px solid #9C8CC8', borderRadius:6, background:'linear-gradient(#F4F0FD,#DDD3F2)', color:'#4C3B78', fontSize:11.5, fontWeight:700, cursor:'pointer', fontFamily:"'Poppins'", boxShadow:'inset 0 1px 0 rgba(255,255,255,0.95), 0 2px 4px rgba(70,52,120,0.26)' }}>Edit</button>
                        {r.lifecycleStatus !== 'Cancelled' && (
                          <button onClick={() => { if (!hasEditAccess(user, 'po_so_lifecycle', 'po_tracker_cancel')) return notifyEditBlocked(); openAction(r, 'cancel'); }} style={{ padding:'6px 14px', border:'1px solid #D3A98C', borderRadius:6, background:'linear-gradient(#FBF0E4,#F0DBC4)', color:'#7A4E20', fontSize:11.5, fontWeight:700, cursor:'pointer', fontFamily:"'Poppins'", boxShadow:'inset 0 1px 0 rgba(255,255,255,0.95), 0 2px 4px rgba(70,52,120,0.2)' }}>Cancel</button>
                        )}
                        <button onClick={() => { if (!hasEditAccess(user, 'po_so_lifecycle', 'po_tracker_delete')) return notifyEditBlocked(); openAction(r, 'delete'); }} style={{ padding:'6px 14px', border:'1px solid #DDA39D', borderRadius:6, background:'linear-gradient(#FBE7E5,#F0CCC7)', color:'#A33B33', fontSize:11.5, fontWeight:700, cursor:'pointer', fontFamily:"'Poppins'", boxShadow:'inset 0 1px 0 rgba(255,255,255,0.95), 0 2px 4px rgba(70,52,120,0.2)' }}>Delete</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {!loading && rows.length === 0 && <div style={{ padding:'46px 16px', textAlign:'center', fontSize:14, color:'#8C82A0' }}>No purchase orders match your filters.</div>}
      </div>

      {/* EDIT / ADD MODAL */}
      {(editPo || adding) && (
        <div onClick={closeModal} style={{ position:'fixed', inset:0, background:'rgba(43,35,64,0.45)', display:'flex', alignItems:'center', justifyContent:'center', padding:22, zIndex:80 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width:580, maxWidth:'100%', maxHeight:'88vh', overflowY:'auto', borderRadius:22, background:CLAY, boxShadow:'13px 13px 32px rgba(60,45,110,0.45), -8px -8px 21px rgba(255,255,255,0.55)' }}>
            <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:15, padding:'22px 26px 18px' }}>
              <div>
                <div style={{ fontSize:11, fontWeight:700, letterSpacing:'0.14em', color:'#B08A3E' }}>{adding ? 'NEW PURCHASE ORDER' : 'EDITING PO'}</div>
                <div style={{ fontSize:25, fontWeight:600, color:TEXT, marginTop:6, letterSpacing:'-0.02em' }}>{adding ? 'Add PO Manually' : editPo}</div>
                {!adding && <div style={{ fontSize:12.5, color:'#8C82A0', marginTop:3 }}>{draft.party}</div>}
              </div>
              <button onClick={closeModal} style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', width:34, height:34, border:'none', borderRadius:12, background:CLAY, color:MUTED, fontSize:17, cursor:'pointer', fontFamily:"'Poppins'", boxShadow:RAISED }}>×</button>
            </div>

            <div style={{ padding:'0 26px 20px', display:'grid', gridTemplateColumns:'1fr 1fr', gap:'15px 17px' }}>
              <div style={{ display:'flex', flexDirection:'column', gap:7 }}>
                <span style={label}>PO DATE</span>
                <input type="date" value={draft.date || ''} onChange={(e) => setD('date', e.target.value)} style={field} />
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:7 }}>
                <span style={label}>PO NUMBER *</span>
                <input type="text" placeholder={adding ? 'Required' : ''} value={draft.po || ''} onChange={(e) => setD('po', e.target.value)} style={field} />
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:7 }}>
                <span style={label}>PARTY NAME</span>
                <input type="text" placeholder={adding ? 'Vendor / Platform name' : ''} value={draft.party || ''} onChange={(e) => setD('party', e.target.value)} style={field} />
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:7 }}>
                <span style={label}>LOCATION</span>
                <input type="text" placeholder={adding ? 'City / Location' : ''} value={draft.loc || ''} onChange={(e) => setD('loc', e.target.value)} style={field} />
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:7 }}>
                <span style={label}>PO UNITS</span>
                <input type="number" placeholder={adding ? '0' : ''} value={draft.units ?? ''} onChange={(e) => setD('units', e.target.value)} style={field} />
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:7 }}>
                <span style={label}>PO AMOUNT</span>
                <input type="number" placeholder={adding ? '0.00' : ''} value={draft.amount ?? ''} onChange={(e) => setD('amount', e.target.value)} style={field} />
              </div>
              <div style={{ gridColumn:'1 / -1', display:'flex', flexDirection:'column', gap:7 }}>
                <span style={label}>PO EXPIRY DATE</span>
                <input type="date" value={draft.expiry || ''} onChange={(e) => setD('expiry', e.target.value)} style={field} />
              </div>
            </div>

            {saveMsg && (
              <div style={{ margin:'0 26px 14px', padding:'9px 13px', borderRadius:11, background:'#F3D9CE', color:'#8A3E2B', fontSize:11.5, fontWeight:600 }}>{saveMsg}</div>
            )}

            <div style={{ display:'flex', gap:11, padding:'17px 26px 23px', borderTop:'1px solid rgba(120,102,168,0.18)' }}>
              <button onClick={save} disabled={saving} style={{ padding:'11px 22px', border:'none', borderRadius:15, background:TEXT, color:'#fff', fontWeight:600, fontSize:13, cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.65 : 1, fontFamily:"'Poppins'", boxShadow:'6px 6px 13px rgba(43,35,64,0.34), -4px -4px 9px rgba(255,255,255,0.8)' }}>
                {saving ? 'Saving…' : (adding ? 'Add PO' : 'Save Changes')}
              </button>
              <button onClick={closeModal} style={{ padding:'11px 22px', border:'none', borderRadius:15, background:CLAY, color:MUTED, fontWeight:600, fontSize:13, cursor:'pointer', fontFamily:"'Poppins'", boxShadow:RAISED }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* CANCEL / DELETE CONFIRM MODAL */}
      {actionPo && (
        <div onClick={closeAction} style={{ position:'fixed', inset:0, background:'rgba(43,35,64,0.45)', display:'flex', alignItems:'center', justifyContent:'center', padding:22, zIndex:80 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width:440, maxWidth:'100%', borderRadius:22, background:CLAY, boxShadow:'13px 13px 32px rgba(60,45,110,0.45), -8px -8px 21px rgba(255,255,255,0.55)', padding:'22px 26px 24px' }}>
            <div style={{ fontSize:11, fontWeight:700, letterSpacing:'0.14em', color: actionPo.kind === 'delete' ? '#A33B33' : '#7A4E20' }}>
              {actionPo.kind === 'delete' ? 'DELETE PO' : 'CANCEL PO'}
            </div>
            <div style={{ fontSize:19, fontWeight:600, color:TEXT, marginTop:6, marginBottom:12, letterSpacing:'-0.01em' }}>
              {actionPo.kind === 'delete' ? `Delete ${actionPo.r.po}?` : `Cancel ${actionPo.r.po}?`}
            </div>
            <div style={{ fontSize:12.5, color:MUTED, lineHeight:1.5, marginBottom:14 }}>
              {actionPo.kind === 'delete'
                ? 'This moves the PO and its line items to the deleted-records archive. It will no longer appear anywhere in PO Tracker.'
                : 'This marks the PO Cancelled — it stays visible in PO Tracker with that status.'}
            </div>
            {!['Open', 'Awaiting Distributor Approval'].includes(actionPo.r.lifecycleStatus) && (
              <div style={{ padding:'9px 13px', borderRadius:11, background:'#F3D9CE', color:'#8A3E2B', fontSize:11.5, fontWeight:600, marginBottom:14 }}>
                Heads up — this PO already has a linked {actionPo.r.lifecycleStatus}. {actionPo.kind === 'delete' ? 'Deleting' : 'Cancelling'} it won't undo those downstream records.
              </div>
            )}
            <div style={{ display:'flex', flexDirection:'column', gap:7 }}>
              <span style={label}>REASON *</span>
              <textarea value={actionReason} onChange={(e) => setActionReason(e.target.value)} rows={3}
                placeholder={actionPo.kind === 'delete' ? 'Why is this PO being deleted?' : 'Why is this PO being cancelled?'}
                style={{ ...field, resize:'vertical', fontFamily:UI_FONT }} />
            </div>
            {actionMsg && (
              <div style={{ marginTop:12, padding:'9px 13px', borderRadius:11, background:'#F3D9CE', color:'#8A3E2B', fontSize:11.5, fontWeight:600 }}>{actionMsg}</div>
            )}
            <div style={{ display:'flex', gap:11, marginTop:18 }}>
              <button onClick={submitAction} disabled={actionSaving} style={{ padding:'11px 22px', border:'none', borderRadius:15, background: actionPo.kind === 'delete' ? '#A33B33' : '#7A4E20', color:'#fff', fontWeight:600, fontSize:13, cursor: actionSaving ? 'default' : 'pointer', opacity: actionSaving ? 0.65 : 1, fontFamily:"'Poppins'" }}>
                {actionSaving ? 'Working…' : (actionPo.kind === 'delete' ? 'Yes, Delete' : 'Yes, Cancel')}
              </button>
              <button onClick={closeAction} style={{ padding:'11px 22px', border:'none', borderRadius:15, background:CLAY, color:MUTED, fontWeight:600, fontSize:13, cursor:'pointer', fontFamily:"'Poppins'", boxShadow:RAISED }}>Back</button>
            </div>
          </div>
        </div>
      )}

      {/* POD MODAL */}
      {podModalPo && (
        <div onClick={() => setPodModalPo(null)} style={{ position:'fixed', inset:0, background:'rgba(43,35,64,0.45)', display:'flex', alignItems:'center', justifyContent:'center', padding:22, zIndex:80 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width:440, maxWidth:'100%', borderRadius:22, background:CLAY, boxShadow:'13px 13px 32px rgba(60,45,110,0.45), -8px -8px 21px rgba(255,255,255,0.55)', padding:'22px 26px 24px' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
              <div style={{ fontSize:19, fontWeight:600, color:TEXT, letterSpacing:'-0.01em' }}>POD — {podModalPo}</div>
              <button onClick={() => setPodModalPo(null)} style={{ border:'none', background:'transparent', fontSize:18, color:MUTED, cursor:'pointer' }}>✕</button>
            </div>
            <PodModalBody poNo={podModalPo} />
          </div>
        </div>
      )}

      {toast && (
        <div style={{ position:'fixed', bottom:22, right:22, padding:'11px 19px', borderRadius:15, background:'#9FC7A6', color:'#1F3527', fontSize:13, fontWeight:600, boxShadow:'7px 7px 16px rgba(90,130,100,0.34), -5px -5px 13px rgba(255,255,255,0.7)', zIndex:60 }}>PO Number copied</div>
      )}
    </div>
  );
}

// Fetches the same grn-management?action=pod endpoint GRN Management's
// PanelPod uses — no new backend needed, this PO's PODs are already there.
function PodModalBody({ poNo }) {
  const [pods, setPods] = useState(null);

  useEffect(() => {
    setPods(null);
    fetch(`${API_BASE}/grn-management?action=pod&po_no=${encodeURIComponent(poNo)}`)
      .then(res => res.json())
      .then(data => setPods(data.pods || []))
      .catch(() => setPods([]));
  }, [poNo]);

  if (pods === null) return <div style={{ fontSize:12.5, color:MUTED }}>Loading…</div>;
  if (pods.length === 0) return <div style={{ fontSize:12.5, color:MUTED }}>No POD available yet.</div>;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
      {pods.map(p => (
        <div key={p.waybill_no} style={{ display:'flex', alignItems:'center', gap:10, background:'#EEE9F8', borderRadius:10, padding:'10px 12px' }}>
          <div style={{ flex:1, minWidth:0, fontSize:12.5, fontWeight:700, color:'#4A3777', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
            {p.invoice_no || p.waybill_no}
          </div>
          <a href={p.view_url} target="_blank" rel="noreferrer" style={{ display:'inline-flex', alignItems:'center', color:'#6E58A6' }} title="Open POD"><ExternalLink size={16} /></a>
          <a href={p.view_url} download style={{ display:'inline-flex', alignItems:'center', color:'#6E58A6' }} title="Download POD"><Download size={16} /></a>
        </div>
      ))}
    </div>
  );
}
